/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { VariedLineRefObj } from './VariedLineRefObj';
import { VariedAreaRefObj } from './VariedAreaRefObj';
import { ConstantLineRefObj } from './ConstantLineRefObj';
import { ConstantAreaRefObj } from './ConstantAreaRefObj';
import { Group, Scale } from '../utils/UNSAFE_visTypes/chart';
import { ReferenceAreaData, ReferenceLineData } from '../utils/UNSAFE_visTypes/chart/refObj.types';

type ReferenceLineAreaProps = (ReferenceLineData | ReferenceAreaData) & {
  xScale: Scale;
  yScale: Scale;
  orientation?: 'horizontal' | 'vertical';
  lineWidth?: number;
  lineStyle?: 'solid' | 'dashed' | 'dotted';
  areaOpacity?: number;
  groups?: Group[];
  xStartIndex?: number;
  xEndIndex?: number;
  isLog?: boolean;
  seriesIndex?: number;
  isXRef?: boolean;
  activeId?: string;
  accessibleLabel?: string;
};

export function ReferenceLineArea(props: ReferenceLineAreaProps) {
  const {
    id,
    type = 'line',
    color,
    lineWidth,
    orientation = 'horizontal',
    areaOpacity,
    items,
    xScale,
    yScale,
    groups = [],
    xStartIndex = 0,
    xEndIndex = 0,
    isLog = false,
    seriesIndex = 0,
    isXRef,
    lineStyle,
    activeId,
    accessibleLabel
  } = props;

  const hasItems = items && items.length > 0;

  //if required scales are missing
  if (!xScale || !yScale || (hasItems && isXRef)) {
    return null;
  }

  const isArea = type === 'area';

  // Which component to use based on whether it has items (varied) or single values (constant)
  if (hasItems) {
    if (isArea) {
      return (
        <VariedAreaRefObj
          id={id}
          xScale={xScale}
          yScale={yScale}
          items={items as { low: number; high: number }[]}
          orientation={orientation}
          areaColor={color}
          areaOpacity={areaOpacity}
          groups={groups}
          xStartIndex={xStartIndex}
          accessibleLabel={accessibleLabel}
          activeId={activeId}
          xEndIndex={xEndIndex}
          isLog={isLog}
          seriesIndex={seriesIndex}
        />
      );
    } else {
      return (
        <VariedLineRefObj
          id={id}
          xScale={xScale}
          yScale={yScale}
          items={items as { value: number }[]}
          orientation={orientation}
          lineColor={color}
          lineStyle={lineStyle}
          lineWidth={lineWidth}
          groups={groups}
          xStartIndex={xStartIndex}
          xEndIndex={xEndIndex}
          accessibleLabel={accessibleLabel}
          activeId={activeId}
          isLog={isLog}
          seriesIndex={seriesIndex}
        />
      );
    }
  } else {
    if (isArea) {
      return (
        <ConstantAreaRefObj
          id={id}
          xScale={xScale}
          yScale={yScale}
          low={(props as ReferenceAreaData).low!}
          high={(props as ReferenceAreaData).high}
          orientation={orientation}
          areaColor={color}
          accessibleLabel={accessibleLabel}
          activeId={activeId}
          isXRef={isXRef}
        />
      );
    } else {
      return (
        <ConstantLineRefObj
          id={id}
          xScale={xScale}
          yScale={yScale}
          value={(props as ReferenceLineData).value!}
          orientation={orientation}
          lineColor={color}
          lineStyle={lineStyle}
          lineWidth={lineWidth}
          accessibleLabel={accessibleLabel}
          activeId={activeId}
          isXRef={isXRef}
        />
      );
    }
  }
}
